import { useEffect, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/layout/AppShell";
import {
  ProductForm,
  componentRowsFromInputs,
} from "@/components/products/ProductForm";
import { useActiveCompany } from "@/lib/company-context";
import { duplicateProduct } from "@/lib/api/products";
import type { CreateProductInput } from "@/lib/api/products";

const searchSchema = z.object({
  /** When provided, fetches the source product and pre-fills the form. */
  duplicate: z.string().uuid().optional(),
});

export const Route = createFileRoute("/companies/$companyId/productos/new")({
  component: NewProductPage,
  validateSearch: searchSchema,
});

function NewProductPage() {
  return (
    <RequireAuth>
      <AppShell>
        <NewProductContent />
      </AppShell>
    </RequireAuth>
  );
}

/**
 * Maps the API draft (CreateProductInput) onto the ProductForm's FormState
 * shape. Only fields the form actually understands are propagated; the form
 * applies its own defaults for anything missing.
 */
function draftToFormInitial(draft: CreateProductInput) {
  return {
    kind: draft.kind,
    sku: draft.sku ?? "",
    standardCode: draft.standardCode ?? "",
    standardCodeScheme: draft.standardCodeScheme ?? "UNSPSC",
    name: draft.name,
    description: draft.description ?? "",
    brandName: draft.brandName ?? "",
    modelName: draft.modelName ?? "",
    unitCode: draft.unitCode ?? "EA",
    unitPrice: draft.unitPrice,
    priceIncludesTax: draft.priceIncludesTax ?? false,
    defaultTaxType: draft.defaultTaxType ?? "01",
    defaultTaxPercent: String(draft.defaultTaxPercent ?? 19),
    isExempt: draft.isExempt ?? false,
    isExcluded: draft.isExcluded ?? false,
    isStockable: draft.isStockable ?? false,
    notes: draft.notes ?? "",
    incomeAccountCode: draft.incomeAccountCode ?? "",
    components: componentRowsFromInputs(draft.components ?? []),
  } as const;
}

function NewProductContent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyId } = Route.useParams();
  const { duplicate } = Route.useSearch();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Duplicate flow — fetch the draft once and feed it into the form.
  const [initial, setInitial] = useState<ReturnType<
    typeof draftToFormInitial
  > | null>(null);
  const [loadingDraft, setLoadingDraft] = useState(!!duplicate);

  useEffect(() => {
    let cancelled = false;
    if (!duplicate) {
      setInitial(null);
      setLoadingDraft(false);
      return;
    }
    setLoadingDraft(true);
    duplicateProduct(companyId, duplicate)
      .then((resp) => {
        if (cancelled) return;
        setInitial(draftToFormInitial(resp.draft));
        setLoadingDraft(false);
      })
      .catch(() => {
        if (cancelled) return;
        // Silent fall back to empty form — toast lives in the dialog where
        // the duplicate flow originated.
        setInitial(null);
        setLoadingDraft(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, duplicate]);

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4">
      <div>
        <Link
          to="/companies/$companyId/productos"
          params={{ companyId }}
          className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          {t("products.new.back")}
        </Link>
      </div>
      {loadingDraft ? (
        <div className="flex items-center justify-center rounded-lg border bg-card py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : (
        <ProductForm
          mode={{ kind: "create", companyId }}
          initial={initial ?? undefined}
          onCreated={(created) => {
            void navigate({
              to: "/companies/$companyId/productos/$productId",
              params: { companyId, productId: created.id },
            });
          }}
        />
      )}
    </div>
  );
}
